"use client";

import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  bastionButtonStyle,
  FONT_BASTION,
} from "@/lib/bastion-button";
import type { TreasuryYieldPoint } from "@/lib/macro/us-treasury-yield";
import "./TreasuryYieldChart.css";

const YIELD_SERIES_COLOR = "#F2C94C";

type RangeKey = "all" | "100y" | "50y" | "25y";

const RANGES: Array<{
  key: RangeKey;
  label: string;
  years: number | null;
}> = [
  { key: "all", label: "All", years: null },
  { key: "100y", label: "100Y", years: 100 },
  { key: "50y", label: "50Y", years: 50 },
  { key: "25y", label: "25Y", years: 25 },
];

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatYield(value: number): string {
  return `${value.toFixed(2)}%`;
}

export default function TreasuryYieldChart({
  data,
  longRunAveragePct,
}: {
  data: TreasuryYieldPoint[];
  longRunAveragePct: number;
}) {
  const [range, setRange] = useState<RangeKey>("all");

  const filtered = useMemo(() => {
    const selected = RANGES.find((item) => item.key === range)!;
    const years = selected.years;
    if (years === null) return data;
    const lastYear = data.at(-1)?.year ?? new Date().getUTCFullYear();
    return data.filter((point) => point.year >= lastYear - years + 1);
  }, [data, range]);

  const domain = useMemo<[number, number]>(() => {
    const values = filtered.map((point) => point.yieldPct);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max(0.5, (max - min) * 0.08);
    return [
      Math.max(0, Math.floor((min - padding) * 2) / 2),
      Math.ceil((max + padding) * 2) / 2,
    ];
  }, [filtered]);

  const latest = filtered.at(-1);
  const badgeAnchor = useMemo(() => {
    if (filtered.length === 0) return { xRatio: 0.18, yRatio: 0.5 };

    const [min, max] = domain;
    const yRatio = (max - longRunAveragePct) / (max - min || 1);
    const firstYear = filtered[0].year;
    const lastYear = filtered[filtered.length - 1].year;
    const span = lastYear - firstYear || 1;
    const leftWindow = filtered.filter(
      (point) => point.year <= firstYear + span * 0.45
    );
    const search = leftWindow.length > 0 ? leftWindow : filtered;
    const safest = search.reduce((best, point) => {
      const gap = Math.abs(point.yieldPct - longRunAveragePct);
      const bestGap = Math.abs(best.yieldPct - longRunAveragePct);
      return gap > bestGap ? point : best;
    });

    return {
      xRatio: (safest.year - firstYear) / span,
      yRatio,
    };
  }, [domain, filtered, longRunAveragePct]);

  const xTicks = useMemo(() => {
    if (filtered.length <= 8) return filtered.map((point) => point.year);

    const firstYear = filtered[0].year;
    const lastYear = filtered[filtered.length - 1].year;
    const steps = 7;
    const ticks = Array.from({ length: steps + 1 }, (_, index) =>
      index === steps
        ? lastYear
        : Math.round(firstYear + ((lastYear - firstYear) * index) / steps)
    );

    return Array.from(new Set(ticks));
  }, [filtered]);

  return (
    <div className="treasury-yield-chart">
      <div className="treasury-yield-chart__toolbar">
        <div className="treasury-yield-chart__identity">
          <div className="treasury-yield-chart__code">
            <span>UST10Y</span>
            <span className="treasury-yield-chart__latest">
              {latest ? formatYield(latest.yieldPct) : ""}
            </span>
          </div>
        </div>

        <div className="treasury-yield-chart__ranges bastion-scrollbar">
          {RANGES.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setRange(item.key)}
              style={{
                ...bastionButtonStyle({
                  color: "#DCE2EC",
                  isSelected: range === item.key,
                  height: 28,
                  paddingX: 10,
                }),
                fontFamily: FONT_BASTION,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="treasury-yield-chart__canvas">
        <ResponsiveContainer>
          <LineChart
            data={filtered}
            margin={{ top: 14, right: 22, bottom: 2, left: 0 }}
          >
            <CartesianGrid
              stroke="rgba(255,255,255,0.055)"
              vertical={false}
            />
            <XAxis
              dataKey="year"
              ticks={xTicks}
              interval={0}
              minTickGap={24}
              padding={{ left: 8, right: 8 }}
              tick={{
                fill: "rgba(255,255,255,0.72)",
                fontFamily: FONT_BASTION,
                fontSize: 12,
              }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
            />
            <YAxis
              domain={domain}
              tickFormatter={(value: number) => `${value}%`}
              tick={{
                fill: "rgba(255,255,255,0.72)",
                fontFamily: FONT_BASTION,
                fontSize: 12,
              }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip
              cursor={{
                stroke: "rgba(255,255,255,0.18)",
                strokeWidth: 1,
              }}
              content={<YieldTooltip />}
              isAnimationActive={false}
              animationDuration={0}
              wrapperStyle={{ outline: "none", pointerEvents: "none" }}
            />
            <Line
              type="linear"
              dataKey="yieldPct"
              name="10-year yield"
              stroke={YIELD_SERIES_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 4,
                fill: YIELD_SERIES_COLOR,
                stroke: "#1a1a1a",
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />
            <ReferenceLine
              y={longRunAveragePct}
              stroke="rgba(255,255,255,0.28)"
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
        <div
          className="treasury-yield-chart__avg-badge"
          style={{
            left: `calc(50px + (100% - 72px) * ${badgeAnchor.xRatio})`,
            top: `calc(14px + (100% - 44px) * ${badgeAnchor.yRatio})`,
          }}
        >
          Long-run avg {longRunAveragePct.toFixed(2)}%
        </div>
      </div>
    </div>
  );
}

function YieldTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    payload: TreasuryYieldPoint;
  }>;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  const isLatest = point.observationType === "latest_daily";

  return (
    <div
      style={{
        minWidth: 178,
        padding: "9px 11px",
        background: "#1a1a1a",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8,
        boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
        fontFamily: FONT_BASTION,
      }}
    >
      <div
        style={{
          marginBottom: 5,
          color: "rgba(255,255,255,0.5)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {isLatest ? formatDate(point.observationDate) : point.year}
      </div>
      <div
        style={{
          color: "#ffffff",
          fontFamily: FONT_BASTION,
          fontSize: 18,
          fontWeight: 700,
        }}
      >
        {formatYield(point.yieldPct)}
      </div>
      <div
        style={{
          marginTop: 4,
          color: isLatest ? "#3bd576" : "rgba(255,255,255,0.42)",
          fontSize: 10,
        }}
      >
        {isLatest ? "Latest Treasury reading" : "Annual historical observation"}
      </div>
    </div>
  );
}
